import { Button, DrawerBody, DrawerFooter, DrawerHeader, DrawerHeaderTitle, OverlayDrawer, Spinner, Textarea } from "@fluentui/react-components";
import { Dismiss24Regular } from "@fluentui/react-icons";
import { connect } from "react-redux";
import { useEffect, useMemo, useRef, useState } from "react";

// component functions
import * as ComponentFunctions from './Payment.Form.Function';
import PaymentImputationsComponent from '../PaymentImputations/PaymentImputations.Component';
import { ImputationPicker } from './Payment.Imputation.Picker';
import { candidateDebts, inPaymentCurrency, parseAmount, suggestedCurrencies, validatePaymentForm } from "../../CashFlow.Form.Logic";
import { AmountInput, CurrencyChips, FieldLabel, MoreData, PartyChip, Step } from "../../CashFlow.Form.Bits";
import { MEDICAL_CENTER_NAME } from "../../General/Cashflow.General.Functions";
import { currencyOf } from "../../General/Cashflow.Dashboard.Functions";

// components
import { DateTimeInput } from "../../../../commons/component/Inputs/DateTimeInput/DateTimeInput";
import PersonSelectorComponent from "../../../../commons/component/Inputs/PersonSelector/PersonSelector.Component";
import ProtocolSelectorComponent from "../../../../commons/component/Inputs/ProtocolSelector/ProtocolSelectorComponent";
import { formatCurrencyAmountWithIssue } from "../../../../commons/currency/Currency.Functions";

// models
import { Person } from "../../../../models/Entities/Person/Person";
import { Protocol } from "../../../../models/Entities/Protocols/Protocol";
import { NewPayment } from "../../../../models/Entities/CashFlow/Payment/NewPayment";
import { Payment } from "../../../../models/Entities/CashFlow/Payment/Payment";
import { DebtPaymentImputation } from "../../../../models/Entities/CashFlow/Payment/DebtPaymentImputation";

// styles
import './Payment.Form.Styles.css';

/**
 * Registro de un cobro o un pago. El centro es siempre una de las partes; se
 * pregunta la otra, el importe y a qué deudas se aplica. Desde una deuda, todo
 * viene precargado.
 */
const PaymentFormComponent = (props: ComponentFunctions.Props) => {

    const isReceived = props.FormType === ComponentFunctions.FormType.receiver;
    const isEdit = !!props.CurrentPayment;

    // ── Estado ──
    const [counterpart, setCounterpart] = useState<Person | undefined>(undefined);
    const [currency, setCurrency] = useState<string | undefined>(undefined);
    const [amountText, setAmountText] = useState<string>('');
    const [amount, setAmount] = useState<number>(0);
    const [date, setDate] = useState<Date | undefined>(new Date());
    const [imputations, setImputations] = useState<DebtPaymentImputation[]>([]);
    const [invoiceText, setInvoiceText] = useState<string>('');
    const [retentionText, setRetentionText] = useState<string>('');
    const [bankText, setBankText] = useState<string>('');
    const [protocolId, setProtocolId] = useState<number | undefined>(undefined);
    const [comments, setComments] = useState<string>('');
    const [submitted, setSubmitted] = useState<boolean>(false);
    const [currencyTouched, setCurrencyTouched] = useState<boolean>(false);

    const ownerPerson: Person = { personIdentifier: props.OwnerId, id: 0, isDeleted: false, personType: 0, businessName: MEDICAL_CENTER_NAME } as Person;
    const personOf = (identifier: string | undefined) => ComponentFunctions.GetPersonByIdentifier(props.Persons, props.OwnerId, identifier || '');

    // ── Carga ──
    useEffect(() => {
        if (!props.isOpen) return;
        props.GetAllPersons();
        if (props.PaymentId && !props.CurrentPayment) props.GetPaymentById(props.PaymentId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.isOpen, props.PaymentId]);

    // Al abrir en alta: arranca con lo que ya se sabe.
    useEffect(() => {
        if (!props.isOpen || props.PaymentId) return;
        setSubmitted(false);
        setCurrencyTouched(false);
        const debt = props.PrefillDebt;
        const counterpartId = debt
            ? (isReceived ? debt.debtor : debt.creditor)
            : (props.FixedCounterpart?.personIdentifier ?? props.PrefillCounterpartId);
        setCounterpart(personOf(counterpartId));
        const prefillCurrency = debt ? currencyOf(debt) : undefined;
        setCurrency(prefillCurrency);
        // Con varias deudas: se suman las de la misma moneda que la primera y se
        // imputa cada una por su pendiente; las de otra moneda quedan como candidatas.
        const round = (value: number) => Math.round(value * 100) / 100;
        const sameCurrency = (props.PrefillDebts || []).filter((item) => currencyOf(item) === prefillCurrency && (item.pendingAmount ?? 0) > 0);
        const pending = round(sameCurrency.reduce((acc, item) => acc + (item.pendingAmount ?? 0), 0));
        setAmount(pending);
        setAmountText(pending > 0 ? String(pending) : '');
        setImputations(sameCurrency.map((item) => ({ debtId: item.id, amount: round(item.pendingAmount ?? 0), exchangeRate: 1 })));
        setDate(new Date());
        setInvoiceText('');
        setRetentionText('');
        setBankText('');
        // El protocolo se hereda si todas las deudas precargadas son del mismo;
        // así el pago queda como salida de plata de ese protocolo.
        const protocols = Array.from(new Set((props.PrefillDebts || []).map((item) => item.protocolId).filter((id): id is number => !!id)));
        setProtocolId(protocols.length === 1 ? protocols[0] : undefined);
        setComments('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.isOpen, (props.PrefillDebts || []).map((item) => item.id).join(','), props.FixedCounterpart?.personIdentifier, props.PrefillCounterpartId, props.Persons.length]);

    // Edición: se completa con el pago.
    useEffect(() => {
        const payment = props.CurrentPayment;
        if (!payment) return;
        setCounterpart(personOf(isReceived ? payment.payer : payment.receiver));
        setCurrency(payment.currencyCode);
        setAmount(payment.amount);
        setAmountText(String(payment.amount));
        setDate(payment.paymentDate ? new Date(payment.paymentDate) : undefined);
        setInvoiceText(payment.invoiceAmount ? String(payment.invoiceAmount) : '');
        setRetentionText(payment.retentionAmount ? String(payment.retentionAmount) : '');
        setBankText(payment.bankChargesAmount ? String(payment.bankChargesAmount) : '');
        setProtocolId(payment.protocolId ?? undefined);
        setComments(payment.comments || '');
        setImputations([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.CurrentPayment?.id, props.Persons.length]);

    useEffect(() => {
        if (props.isSaved && submitted) {
            setSubmitted(false);
            props.onClose();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.isSaved]);

    // Segundo camino de cierre: terminó de guardar (el spinner bajó) sin error.
    // No depende de que la bandera de éxito cambie de valor, que es lo que
    // dejaba el drawer abierto cuando ya venía en true de un guardado anterior.
    const wasSavingRef = useRef(false);
    useEffect(() => {
        const finished = wasSavingRef.current && !props.isSaving;
        wasSavingRef.current = props.isSaving;
        if (finished && submitted && !props.failOnSaving) {
            setSubmitted(false);
            props.onClose();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.isSaving, props.failOnSaving]);

    useEffect(() => {
        if (props.failOnSaving) setSubmitted(false);
    }, [props.failOnSaving]);

    // ── Sugerencias y deudas candidatas ──
    const suggested = useMemo(
        () => suggestedCurrencies({ counterpartId: counterpart?.personIdentifier, debts: props.AllDebts, payments: props.AllPayments }),
        [counterpart?.personIdentifier, props.AllDebts, props.AllPayments]
    );

    useEffect(() => {
        if (!isEdit && !currencyTouched && !props.PrefillDebt) setCurrency(suggested[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [suggested.join(','), isEdit]);

    const candidates = useMemo(
        () => candidateDebts(props.CandidateDebts, counterpart?.personIdentifier, (debt) => (isReceived ? debt.debtor : debt.creditor)),
        [props.CandidateDebts, counterpart?.personIdentifier, isReceived]
    );

    // Si cambia la contraparte o la moneda, lo imputado ya no aplica.
    const candidateKey = candidates.map((debt) => debt.id).join(',');
    useEffect(() => {
        setImputations((current) => current.filter((item) => candidates.some((debt) => debt.id === item.debtId)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [candidateKey]);

    // ── Validación ──
    const errors = validatePaymentForm({ counterpartId: counterpart?.personIdentifier, currency, amount, date, imputations });
    const showError = (key: string) => (submitted ? errors[key] : undefined);
    const canSave = Object.keys(errors).length === 0 && !props.isSaving;

    // ── Guardar ──
    const save = () => {
        setSubmitted(true);
        if (Object.keys(errors).length > 0 || !counterpart || !currency || !date) return;

        const payer = isReceived ? counterpart.personIdentifier : props.OwnerId;
        const receiver = isReceived ? props.OwnerId : counterpart.personIdentifier;
        const optional = (text: string) => { const value = parseAmount(text); return value > 0 ? value : undefined; };

        const base = {
            payer, receiver, currencyCode: currency, amount,
            invoiceAmount: optional(invoiceText), retentionAmount: optional(retentionText), bankChargesAmount: optional(bankText),
            paymentDate: date, imputationDate: date,
            comments: comments || undefined, protocolId: protocolId ?? null
        };

        if (!props.CurrentPayment) {
            // El picker trabaja en moneda de la deuda; la API espera el importe en moneda del pago (lo aplicado a la deuda es amount / exchangeRate).
            const debtsPaymentImputation = imputations.map((item) => ({ ...item, amount: Math.round(inPaymentCurrency(item) * 100) / 100 }));
            const newPayment: NewPayment = { ...base, debtsPaymentImputation };
            props.CreatePayment(newPayment);
        } else {
            const payment: Payment = { ...props.CurrentPayment, ...base, debtsPaymentImputation: [] };
            props.UpdatePayment(payment);
        }
    };

    const close = () => { setSubmitted(false); props.onClose(); };

    // ── Textos ──
    const kind = isReceived ? 'cobro' : 'pago';
    const kindLabel = isReceived ? 'Cobro' : 'Pago';
    const title = isEdit ? `Editar ${kind}` : (isReceived ? 'Registrar cobro' : 'Registrar pago');
    const whoQuestion = isReceived ? '¿Quién nos paga?' : '¿A quién le pagamos?';
    const counterpartRole = isReceived ? 'Paga' : 'Cobra';
    const centerRole = isReceived ? 'Cobra' : 'Paga';
    const counterpartName = counterpart ? props.NameOf(counterpart.personIdentifier) : undefined;
    const isCounterpartFixed = !!props.FixedCounterpart || !!props.PrefillDebt;

    const renderForm = () => (
        <div className="cff-root">
            {props.failOnSaving && (
                <div className="cff-banner" role="alert">
                    <strong>No se pudo guardar el {kind}.</strong>
                    <span>{props.error?.ErrorMessage || 'Probá de nuevo.'}</span>
                    {props.error?.Errors && props.error.Errors.length > 0 && <ul>{props.error.Errors.map((item) => <li key={item}>{item}</li>)}</ul>}
                </div>
            )}

            {props.PrefillDebt && !isEdit && (
                <div className="cff-banner cff-banner--warn">
                    <strong>
                        {props.PrefillDebts.length > 1
                            ? `${kindLabel} de ${props.PrefillDebts.length} deudas`
                            : `${kindLabel} de la deuda #${props.PrefillDebt.id}`}
                        {(() => {
                            const settlements = Array.from(new Set(props.PrefillDebts.map((item) => item.settlementId).filter(Boolean)));
                            return settlements.length > 0 ? ` · ${settlements.map((id) => `LIQ-${id}`).join(', ')}` : '';
                        })()}
                    </strong>
                    <span>Contraparte, moneda e importe ya vienen cargados con lo pendiente. Si {isReceived ? 'cobraste' : 'pagaste'} otra cosa, cambiá el importe.</span>
                </div>
            )}

            {/* 1 · Con quién */}
            <Step n={1} title={whoQuestion} error={showError('counterpart')}>
                <div className="cff-parties">
                    {isEdit || isCounterpartFixed ? (
                        <PartyChip role={counterpartRole} name={counterpartName || '—'} />
                    ) : (
                        <div className="cff-field" style={{ flex: 1, minWidth: 260 }}>
                            <PersonSelectorComponent
                                personSelected={counterpart ? [counterpart.personIdentifier] : []}
                                selectionLimit={1}
                                isRequired
                                fieldLabel={counterpartRole}
                                CustomAditionalPersonsList={[ownerPerson]}
                                onChangeSelection={(selected: Person[]) => setCounterpart(selected[0])}
                            />
                        </div>
                    )}
                    <span className="cff-parties__arrow" aria-hidden>→</span>
                    <PartyChip role={centerRole} name={MEDICAL_CENTER_NAME} />
                </div>
            </Step>

            {/* 2 · Cuánto y cuándo */}
            <Step n={2} title="¿Cuánto y cuándo?" error={showError('amount') || showError('currency') || showError('date')}>
                <div className="cff-field">
                    <FieldLabel text="Moneda" required />
                    <CurrencyChips suggested={suggested} value={currency} onChange={(code) => { setCurrencyTouched(true); setCurrency(code); }} disabled={isEdit} />
                </div>
                <div className="cff-grid">
                    <div className="cff-field">
                        <FieldLabel text="Importe" required />
                        <AmountInput id="payment-amount" value={amountText} currency={currency} autoFocus={!isEdit && !!counterpart && !props.PrefillDebt} error={!!showError('amount')}
                            onChange={(text, value) => { setAmountText(text); setAmount(value); }} />
                        {amount > 0 && currency && <span className="cff-help cff-num">{formatCurrencyAmountWithIssue(amount, currency)}</span>}
                    </div>
                    <div className="cff-field">
                        <FieldLabel text="Fecha" required />
                        <div className="cff-input-box">
                            <DateTimeInput label={undefined} placeHolder="Fecha" selectedDate={date} onSelectDate={setDate} isSubmitted={submitted} isRequired errorMessage={undefined} />
                        </div>
                        <span className="cff-help">Por defecto, hoy.</span>
                    </div>
                </div>
                <MoreData label="Detalle del comprobante (opcional)" open={!!invoiceText || !!retentionText || !!bankText}>
                    <div className="cff-grid">
                        <div className="cff-field">
                            <FieldLabel text="Importe de factura" />
                            <AmountInput value={invoiceText} currency={currency} onChange={(text) => setInvoiceText(text)} />
                        </div>
                        <div className="cff-field">
                            <FieldLabel text="Retenciones" />
                            <AmountInput value={retentionText} currency={currency} onChange={(text) => setRetentionText(text)} />
                        </div>
                        <div className="cff-field">
                            <FieldLabel text="Gastos bancarios" />
                            <AmountInput value={bankText} currency={currency} onChange={(text) => setBankText(text)} />
                        </div>
                    </div>
                </MoreData>
            </Step>

            {/* 3 · Qué deudas cancela */}
            {!isEdit && (
                <Step n={3} title="¿Qué deudas cancela?" error={showError('imputations')}
                    hint={counterpart && currency
                        ? <>Pendientes de {counterpartName}, más viejas primero. Podés dejar parte sin imputar.</>
                        : <>Primero elegí la contraparte y la moneda.</>}>
                    {counterpart && currency ? (
                        <ImputationPicker
                            debts={candidates}
                            paymentAmount={amount}
                            paymentCurrency={currency}
                            counterpartName={counterpartName || ''}
                            value={imputations}
                            onChange={setImputations}
                        />
                    ) : null}
                </Step>
            )}

            <MoreData open={!!protocolId || !!comments}>
                <div className="cff-step">
                    <div className="cff-field">
                        <FieldLabel text="Protocolo" help="Si el movimiento pertenece a un estudio." />
                        <ProtocolSelectorComponent
                            selectionLimit={1}
                            selectedProtocols={protocolId === undefined ? [] : [{ id: protocolId } as Protocol]}
                            onChangeSelection={(selected: Protocol[] | undefined) => setProtocolId((selected || [])[0]?.id)}
                        />
                    </div>
                    <div className="cff-field">
                        <FieldLabel text="Comentarios" help="Referencia de la transferencia, número de recibo, lo que ayude a encontrarlo después." />
                        <Textarea value={comments} onChange={(_e, data) => setComments(data.value)} resize="vertical" />
                    </div>
                </div>
            </MoreData>

            {isEdit && props.PaymentId && (
                <div className="cff-step">
                    <PaymentImputationsComponent paymentId={props.PaymentId} />
                </div>
            )}
        </div>
    );

    const loadingEdit = !!props.PaymentId && !props.CurrentPayment;

    return (
        <OverlayDrawer position="end" open={props.isOpen} size="large" onOpenChange={(_e, data) => { if (!data.open) close(); }}>
            <DrawerHeader>
                <DrawerHeaderTitle action={<Button appearance="subtle" aria-label="Cerrar" icon={<Dismiss24Regular />} onClick={close} />}>
                    <span className="cff-title">{title}</span>
                </DrawerHeaderTitle>
            </DrawerHeader>
            <DrawerBody>
                {loadingEdit ? <div className="cff-state"><Spinner size="small" /> Cargando {kind}…</div> : renderForm()}
            </DrawerBody>
            <DrawerFooter>
                <div className="cff-footer">
                    {amount > 0 && currency && counterpartName && (
                        <span className="cff-footer__summary">
                            {isReceived ? `${counterpartName} paga` : `Pagamos a ${counterpartName}`} <b className="cff-num">{formatCurrencyAmountWithIssue(amount, currency)}</b>
                        </span>
                    )}
                    <button type="button" className="cff-secondary" onClick={close} disabled={props.isSaving}>Cancelar</button>
                    <button type="button" className="cff-primary" onClick={save} disabled={submitted ? !canSave : props.isSaving}>
                        {props.isSaving && <Spinner size="extra-tiny" />} {isEdit ? 'Guardar cambios' : (isReceived ? 'Registrar cobro' : 'Registrar pago')}
                    </button>
                </div>
            </DrawerFooter>
        </OverlayDrawer>
    );
};

export default connect(
    ComponentFunctions.mapStateToProps,
    ComponentFunctions.mapDispatchToProps
)(PaymentFormComponent as any);
